import { useEffect, useState } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";

function Stars({ value, onChange, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={onChange ? () => onChange(i) : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          data-testid={onChange ? `rating-star-${i}` : undefined}
          aria-label={`${i} star`}
        >
          <Star size={size} className={i <= value ? "text-brand" : "text-stone-300"} fill={i <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ product }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ rating: 5, title: "", text: "", photo: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${product.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imgEl = new Image();
      imgEl.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 600 / imgEl.width);
        canvas.width = imgEl.width * scale;
        canvas.height = imgEl.height * scale;
        canvas.getContext("2d").drawImage(imgEl, 0, 0, canvas.width, canvas.height);
        setForm((f) => ({ ...f, photo: canvas.toDataURL("image/jpeg", 0.6) }));
      };
      imgEl.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.text.trim()) { toast.error("Please write a few words"); return; }
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/reviews`, form);
      toast.success("Review posted — thank you!");
      setForm({ rating: 5, title: "", text: "", photo: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-14 border-t border-stone-200 pt-10" data-testid="reviews-section">
      <p className="eyebrow">REVIEWS</p>
      <h2 className="font-heading text-2xl font-semibold mt-1 mb-6">
        What customers say {product.rating_count > 0 && <span className="text-stone-400 text-lg">({product.rating_avg} ★ · {product.rating_count})</span>}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {reviews.length === 0 && <p className="text-sm text-stone-500" data-testid="no-reviews">No reviews yet — be the first.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="border border-stone-200 bg-white p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Stars value={r.rating} size={13} />
                {r.verified && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                    <BadgeCheck size={12} /> Verified buyer
                  </span>
                )}
                <span className="text-xs text-stone-400 ml-auto">{fmtDate(r.created_at)}</span>
              </div>
              {r.title && <p className="font-medium text-sm mt-2">{r.title}</p>}
              <p className="text-sm text-stone-600 mt-1 leading-relaxed">{r.text}</p>
              {r.photo && <img src={r.photo} alt="Customer upload" className="h-24 w-24 object-cover mt-3 border border-stone-200" />}
              <p className="text-xs text-stone-400 mt-2">— {r.name}</p>
            </div>
          ))}
        </div>
        <div className="border border-stone-200 bg-white p-5 h-fit">
          <h3 className="font-heading font-semibold">Write a review</h3>
          {user ? (
            <div className="space-y-3 mt-3">
              <div><Label>Rating</Label><Stars value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size={22} /></div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title-input" /></div>
              <div>
                <Label>Review</Label>
                <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full h-20 border border-stone-200 rounded-sm px-3 py-2 text-sm bg-white" data-testid="review-text-input" />
              </div>
              <div>
                <Label>Photo (optional)</Label>
                <Input type="file" accept="image/*" onChange={onPhoto} data-testid="review-photo-input" />
                {form.photo && <img src={form.photo} alt="" className="h-16 w-16 object-cover mt-2 border border-stone-200" />}
              </div>
              <Button onClick={submit} disabled={busy} className="w-full bg-stone-900 hover:bg-brand text-white btn-press" data-testid="review-submit-btn">
                {busy ? "Posting…" : "Post Review"}
              </Button>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-sm text-stone-500">Sign in to share your review.</p>
              <Button variant="outline" className="mt-3" onClick={() => navigate("/login")} data-testid="review-login-btn">Sign In</Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
